import { useCallback, useEffect, useRef } from 'react';
import { useSetDataAttrs, useSpatialEvent } from 'hooks';
import { animateHorizontalScroll } from '#/utils/scrollAnimation.js';
import styles from './scroll-container.module.css';

export function ScrollContainer({ desc }) {
  const ref = useRef(null);

  useSetDataAttrs(ref, desc.dataset);

  const onSpatialEvent = useCallback((ev) => {
    if (ev.detail.candidate === null) {
      return;
    }

    const {
      detail: { candidate: nextElement, direction },
    } = ev;

    let nextScrollLeft = ref.current.scrollLeft;

    // add/reduce scroll left offset equal to width of the next element
    if (direction === 39) {
      nextScrollLeft += nextElement.clientWidth;
    } else if (direction === 37) {
      nextScrollLeft -= nextElement.clientWidth;
    }

    animateHorizontalScroll(ref.current, nextScrollLeft, 200);
  }, []);

  useSpatialEvent(ref, onSpatialEvent);

  useEffect(() => {
    let timeoutId = null;
    if (ref.current) {
      const isResultOptions = desc.children.some(
        ({ pollState }) => pollState === 'result',
      );
      if (isResultOptions) {
        const votedOption = ref.current.querySelector('[data-voted="true"]');
        if (votedOption) {
          const { offsetLeft: optionLeft, offsetWidth: optionWidth } =
            votedOption;
          const targetScrollLeft =
            optionLeft -
            (optionWidth +
              parseInt(window.getComputedStyle(votedOption).marginRight)) /
              2;
          timeoutId = window.setTimeout(() => {
            animateHorizontalScroll(ref.current, targetScrollLeft, 500);
            timeoutId = null;
          }, 500);
        }
      }
    }
    return () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [desc.children]);

  return (
    <div id={desc.id} className={styles.scrollContainer} ref={ref}>
      {desc.children.map((child) => (
        <child.type key={child.id} desc={child} />
      ))}
    </div>
  );
}
